/**
 * 文档编辑Modal组件
 * 包含Markdown编辑器和预览功能
 */

import React, { useState, useEffect } from 'react';
import { Modal, Input,  Button, Space, Divider, Card  } from 'antd';
// 使用 window.message 代替 antd 静态方法，避免 context 警告
import { SaveOutlined, CloseOutlined, EyeOutlined, EditOutlined, FileTextOutlined } from '@ant-design/icons';
import { useDocumentModalStyles } from '../../styles/components/markdown/DocumentModal';
import MarkdownEditor from './MarkdownEditor';
import MarkdownRenderer from './MarkdownRenderer';
import markdownService from '../../services/markdownService';
import attachmentService from '../../services/attachmentService';
import { markdownToFile, generateDocumentFilename, blobToMarkdown } from '../../utils/markdownUtils';

const DocumentModal = ({ visible, onClose, initialDocument, onSave, attachments = [], displayFiles = [] }) => {
  const { styles } = useDocumentModalStyles();
  const [content, setContent] = useState('');
  const [title, setTitle] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState('saved'); // 'saved', 'saving', 'error'
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [isReadOnly, setIsReadOnly] = useState(false);
  const [editorHeight, setEditorHeight] = useState(600);

    // 计算编辑器高度
  const calculateEditorHeight = () => {
    // 获取窗口高度
    const windowHeight = window.innerHeight;
    
    // 计算可用高度：窗口高度 - Modal的top值(20px) - 标题栏高度(约80px) - footer高度(约60px) - 额外padding和margin(120px)
    const availableHeight = windowHeight - 20 - 80 - 60 - 120;
    
    // 设置最小高度为450px，最大高度为可用高度的80%
    const calculatedHeight = Math.max(450, Math.floor(availableHeight * 0.9));
  
    
    setEditorHeight(calculatedHeight);
  };

  // 当初始文档变化时，更新内容
  useEffect(() => {
    if (initialDocument) {
      // 优先使用fields.content，然后是content，最后是空字符串
      const documentContent = initialDocument.fields?.content || initialDocument.content || '';
      
      setContent(documentContent);
      setTitle(initialDocument.title || initialDocument.fields?.name || '新文档');
      // 检查是否为只读模式
      setIsReadOnly(initialDocument.readOnly || false);
      // 如果是只读模式，默认显示预览
      if (initialDocument.readOnly) {
        setIsPreviewMode(true);
      }
    } else {
      setContent('');
      setTitle('新文档');
      setIsReadOnly(false);
      setIsPreviewMode(false);
    }
  }, [initialDocument]);

  // 监听Modal显示状态，计算编辑器高度
  useEffect(() => {
    if (visible) {
      // 使用setTimeout确保Modal完全渲染后再计算高度
      const timer = setTimeout(() => {
        calculateEditorHeight();
      }, 100);
      
      // 添加窗口大小变化监听
      const handleResize = () => {
        calculateEditorHeight();
      };
      
      window.addEventListener('resize', handleResize);
      
      // 清理监听器和定时器
      return () => {
        clearTimeout(timer);
        window.removeEventListener('resize', handleResize);
      };
    }
  }, [visible]);

  // 处理保存
  const handleSave = async () => {
    if (!content.trim()) {
      window.message?.warning('文档内容不能为空');
      return;
    }
    if (!title.trim()) {
      window.message?.warning('文档标题不能为空');
      return;
    }

    setSaving(true);
    setSaveStatus('saving');

    try {
      let savedDocument = null;

      if (initialDocument?.id) {
        // 更新现有文档 - 使用原始文件名，避免重复时间戳
        const originalFilename = initialDocument.filename || initialDocument.file_name || `${title}.md`;
        const filename = originalFilename.endsWith('.md') ? originalFilename : `${originalFilename}.md`;
        
        // 直接使用原始内容进行base64编码，避免文件转换
  
        const base64Content = btoa(unescape(encodeURIComponent(content)));
        
        
        // 使用正确的markdown字段更新API
        const updatedAttachment = await attachmentService.updateAttachment(initialDocument.id, {
          // 直接传递markdown内容到content字段
          content: content,
          // 文件名使用name字段
          name: filename,
          // 描述
          description: title,
          // 状态
          status: 'published',
          // 同时传递file_content用于后端更新物理文件
          file_content: base64Content,
          file_name: filename,
        });
        
        savedDocument = {
          ...updatedAttachment,
          title: updatedAttachment.file_name || title,
          content: content,
          type: 'markdown',
          content_type: 'markdown',
          file_name: updatedAttachment.file_name,
          size: updatedAttachment.size,
          status: updatedAttachment.status,
          description: updatedAttachment.description,
          metadata: updatedAttachment.metadata,
          url: updatedAttachment.url,
          inserted_at: updatedAttachment.inserted_at,
          updated_at: updatedAttachment.updated_at,
          user_id: updatedAttachment.user_id,
          attachable_id: updatedAttachment.attachable_id,
          attachable_type: updatedAttachment.attachable_type,
          editable: updatedAttachment.editable
        };
        
        window.message?.success('文档更新成功');
      } else {
        // 创建新文档
        const filename = generateDocumentFilename(title);
        const markdownFile = markdownToFile(content, filename);
        
        // 使用附件上传API
        const uploadedAttachment = await attachmentService.uploadAttachment(markdownFile, {
          description: `Markdown文档: ${title}`,
          editable: true, // 使用Markdown编辑器创建
          status: 'published',
          metadata: {
            source: 'markdown_editor',
            category: 'markdown',
            title: title,
            template_id: initialDocument?.template_id,
            content_length: content.length,
            inserted_at: new Date().toISOString()
          }
        });
        
        savedDocument = {
          ...uploadedAttachment,
        };
        
        window.message?.success('文档创建成功');
      }
      
      setSaveStatus('saved');
      
      // 调用父组件的保存回调，传递保存的文档数据
      if (onSave) {
        await onSave(content, title, true, savedDocument);
      }
    } catch (error) {
      console.error('保存文档失败:', error);
      setSaveStatus('error');
      window.message?.error('保存失败，请重试');
    } finally {
      setSaving(false);
    }
  };

  // 处理内容变化
  const handleContentChange = (newContent) => {
    setContent(newContent);
    if (saveStatus === 'saved') {
      setSaveStatus('saving');
      // 自动保存逻辑可以在这里实现
      setTimeout(() => {
        setSaveStatus('saved');
      }, 1000);
    }
  };

  // 处理标题变化
  const handleTitleChange = (e) => {
    setTitle(e.target.value);
    if (saveStatus === 'saved') {
      setSaveStatus('saving');
      setTimeout(() => {
        setSaveStatus('saved');
      }, 1000);
    }
  };

  // 切换预览模式
  const togglePreviewMode = () => {
    setIsPreviewMode(!isPreviewMode);
  };

  // 渲染预览内容
  const renderPreview = () => {
    return (
      <div className={styles.previewContainer}>
        
        <div className={styles.previewContent}>
          <MarkdownRenderer
            content={content}
            height={editorHeight}
            className={styles.markdownPreview}
          />
        </div>
      </div>
    );
  };

  return (
    <Modal
      title={
        <div className={styles.modalHeader}>
          {isReadOnly ? (
            <div className={styles.titleDisplay}>
              <FileTextOutlined className={styles.titleIcon} />
              <h2>{title || '无标题文档'}</h2>
              <span className={styles.readOnlyBadge}>只读模式</span>
            </div>
          ) : (
            <div className={styles.titleEditContainer}>
              <FileTextOutlined className={styles.titleIcon} />
              <div className={styles.titleInputWrapper}>
                <Input
                  className={styles.titleInput}
                  value={title}
                  onChange={handleTitleChange}
                  placeholder="请输入文档标题..."
                  bordered={false}
                  autoFocus
                  size="large"
                />
              </div>
              <span className={`${styles.saveStatus} ${
                saveStatus === 'saving' ? styles.saveStatusSaving : 
                saveStatus === 'error' ? styles.saveStatusError : 
                styles.saveStatusSaved
              }`}>
                {saveStatus === 'saving' ? '正在保存...' : 
                 saveStatus === 'error' ? '保存失败' : 
                 '已保存'}
              </span>
            </div>
          )}
        </div>
      }
      open={visible}
      onCancel={onClose}
      width="90%"
      style={{ top: 20 }}
      footer={
        <div className={styles.modalFooter}>
          <Space>
            {!isReadOnly && (
              <Button
                icon={isPreviewMode ? <EditOutlined /> : <EyeOutlined />}
                onClick={togglePreviewMode}
              >
                {isPreviewMode ? '编辑模式' : '预览模式'}
              </Button>
            )}
            <Button onClick={onClose}>
              {isReadOnly ? '关闭' : '取消'}
            </Button>
            {!isReadOnly && (
              <Button
                type="primary"
                icon={<SaveOutlined />}
                loading={saving}
                onClick={handleSave}
              >
                保存并返回
              </Button>
            )}
          </Space>
        </div>
      }
      className={styles.modal}
    >
      <div className={styles.modalBody}>
        {isReadOnly || isPreviewMode ? (
          renderPreview()
        ) : (
          <div className={styles.editorContainer}>
            {/* Markdown编辑器 */}
            <div className={styles.editorWrapper}>
              <MarkdownEditor
                value={content}
                onChange={handleContentChange}
                onSave={handleSave}
                placeholder="开始编写您的文档内容..."
                height={editorHeight}
                attachments={attachments}
                displayFiles={displayFiles}
              />
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default DocumentModal;
